import React, { useState, useEffect, useCallback, useRef } from "react";
import { Users, Plus, Trash2, Pencil, X, Camera, Cake, Phone, Heart, Loader2, UserPlus } from "lucide-react";
import { familyList, familyAdd, familyUpdate, familyDelete, familyPhotoUrl } from "../lib/api";
import { toast } from "sonner";
import { EyeVisits } from "./EyeVisits";

const EMPTY = { name: "", relation: "", birthday: "", phone: "", notes: "" };

const RELATIONS = ["Wife", "Husband", "Son", "Daughter", "Mother", "Father", "Brother", "Sister", "Best friend", "Grandparent", "Other"];

function Avatar({ member, userId, size = 64 }) {
  const [err, setErr] = useState(false);
  const initials = (member.name || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (member.has_photo && !err) {
    return (
      <img
        src={familyPhotoUrl(member.id, userId)}
        onError={() => setErr(true)}
        alt={member.name}
        style={{ width: size, height: size }}
        className="rounded-2xl object-cover border border-[#00F0FF55] shrink-0"
        data-testid={`family-photo-${member.id}`}
      />
    );
  }
  return (
    <div style={{ width: size, height: size }}
      className="rounded-2xl shrink-0 flex items-center justify-center bg-[#00F0FF11] border border-[#00F0FF33] text-[#00F0FF] font-bold nx-display"
      data-testid={`family-initials-${member.id}`}>
      {initials || <Users className="w-6 h-6" />}
    </div>
  );
}

function MemberForm({ session, member, onClose, onSaved }) {
  const userId = session.user_id;
  const editing = !!member;
  const [f, setF] = useState(member ? {
    name: member.name || "", relation: member.relation || "", birthday: member.birthday || "",
    phone: member.phone || "", notes: member.notes || "",
  } : EMPTY);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(member && member.has_photo ? familyPhotoUrl(member.id, userId) : null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const pick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    setPreview(URL.createObjectURL(file));
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Please enter a name"); return; }
    setSaving(true);
    try {
      if (editing) await familyUpdate(userId, member.id, f, photo);
      else await familyAdd(userId, f, photo);
      toast.success(editing ? `${f.name} updated` : `${f.name} added to the family`);
      onSaved();
    } catch (e) {
      toast.error("Could not save — try again");
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4" data-testid="family-form-modal">
      <div className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-[#060b18] border border-[#00F0FF33] rounded-t-3xl sm:rounded-3xl p-5 nx-display">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[#00F0FF] font-bold text-lg flex items-center gap-2">
            <UserPlus className="w-5 h-5" /> {editing ? "Edit family member" : "Add family member"}
          </h3>
          <button onClick={onClose} data-testid="family-form-close" className="text-cyan-200/60 hover:text-cyan-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-4 mb-5">
          <div className="relative">
            {preview ? (
              <img src={preview} alt="preview" className="w-20 h-20 rounded-2xl object-cover border border-[#00F0FF55]" />
            ) : (
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center bg-[#00F0FF11] border border-[#00F0FF33] text-[#00F0FF]"><Camera className="w-7 h-7" /></div>
            )}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="family-photo-input" />
            <button onClick={() => fileRef.current?.click()} data-testid="family-photo-pick"
              className="px-4 py-2 rounded-full text-xs font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11]">
              {preview ? "Change photo" : "Add a photo"}
            </button>
            <p className="text-[11px] text-cyan-200/40 mt-1.5">So Nexus knows their face</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-cyan-200/50">Name</label>
            <input value={f.name} onChange={set("name")} data-testid="family-name-input" placeholder="e.g. Alia"
              className="w-full mt-1 bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 placeholder:text-cyan-200/25 focus:outline-none focus:border-[#00F0FF66]" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-cyan-200/50">Relationship</label>
            <input value={f.relation} onChange={set("relation")} data-testid="family-relation-input" list="family-relations" placeholder="e.g. Daughter"
              className="w-full mt-1 bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 placeholder:text-cyan-200/25 focus:outline-none focus:border-[#00F0FF66]" />
            <datalist id="family-relations">{RELATIONS.map((r) => <option key={r} value={r} />)}</datalist>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-cyan-200/50">Birthday</label>
              <input value={f.birthday} onChange={set("birthday")} data-testid="family-birthday-input" placeholder="March 15 / 2015-03-15"
                className="w-full mt-1 bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 placeholder:text-cyan-200/25 focus:outline-none focus:border-[#00F0FF66]" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-cyan-200/50">Phone</label>
              <input value={f.phone} onChange={set("phone")} data-testid="family-phone-input" placeholder="+1 ..."
                className="w-full mt-1 bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 placeholder:text-cyan-200/25 focus:outline-none focus:border-[#00F0FF66]" />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-cyan-200/50">What Nexus should know about them</label>
            <textarea value={f.notes} onChange={set("notes")} data-testid="family-notes-input" rows={4} placeholder="Loves football and biryani, allergic to peanuts, studies at Lincoln High, gets anxious before exams..."
              className="w-full mt-1 bg-[#0a1020] border border-[#00F0FF22] rounded-xl px-3 py-2.5 text-cyan-50 placeholder:text-cyan-200/25 focus:outline-none focus:border-[#00F0FF66] resize-none" />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-3 rounded-full text-sm font-bold text-cyan-200/60 border border-[#00F0FF22] hover:bg-[#00F0FF08]">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="family-save-btn"
            className="flex-1 py-3 rounded-full text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-[#5ff7ff] disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />} {editing ? "Save" : "Add to family"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function NexusFamily({ session }) {
  const userId = session.user_id;
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | {} (new) | member (edit)

  const load = useCallback(async () => {
    try {
      const r = await familyList(userId);
      setMembers(r.members || []);
    } catch (e) { toast.error("Could not load family"); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the family circle?`)) return;
    try { await familyDelete(m.id, userId); toast.success(`${m.name} removed`); load(); }
    catch (e) { toast.error("Could not remove"); }
  };

  return (
    <div className="h-full overflow-y-auto bg-[#030712] nx-display" data-testid="nexus-family-panel">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="mb-5 flex justify-center"><EyeVisits userId={session.user_id} /></div>
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <h2 className="text-[#00F0FF] font-bold text-xl flex items-center gap-2"><Users className="w-6 h-6" /> Family Circle</h2>
            <p className="text-cyan-200/50 text-sm mt-1">The people you love. Nexus knows each of them by name and face — he's Uncle Nexus.</p>
          </div>
          <button onClick={() => setForm({})} data-testid="family-add-btn"
            className="px-4 py-2.5 rounded-full text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-[#5ff7ff] flex items-center gap-1.5 shrink-0">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-cyan-200/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : members.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#00F0FF22] rounded-3xl" data-testid="family-empty">
            <Users className="w-10 h-10 text-[#00F0FF66] mx-auto mb-3" />
            <p className="text-cyan-100 font-semibold">No one added yet</p>
            <p className="text-cyan-200/50 text-sm mt-1 mb-4">Add your wife and kids so Nexus becomes part of the family.</p>
            <button onClick={() => setForm({})} data-testid="family-add-empty"
              className="px-5 py-2.5 rounded-full text-sm font-bold text-[#030712] bg-[#00F0FF]">Add the first person</button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="family-list">
            {members.map((m) => (
              <div key={m.id} data-testid={`family-card-${m.id}`}
                className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4 flex gap-3.5 hover:border-[#00F0FF44] transition-colors">
                <Avatar member={m} userId={userId} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-cyan-50 font-bold truncate">{m.name}</p>
                      {m.relation && <p className="text-[#00F0FF] text-xs">{m.relation}</p>}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => setForm(m)} data-testid={`family-edit-${m.id}`} className="p-1.5 rounded-lg text-cyan-200/50 hover:text-cyan-100 hover:bg-[#00F0FF11]"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(m)} data-testid={`family-delete-${m.id}`} className="p-1.5 rounded-lg text-rose-300/60 hover:text-rose-300 hover:bg-rose-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[11px] text-cyan-200/50">
                    {m.birthday && <span className="flex items-center gap-1"><Cake className="w-3 h-3" /> {m.birthday}</span>}
                    {m.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {m.phone}</span>}
                  </div>
                  {m.notes && <p className="text-cyan-100/70 text-xs mt-2 line-clamp-3">{m.notes}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {form !== null && (
        <MemberForm session={session} member={form.id ? form : null} onClose={() => setForm(null)}
          onSaved={() => { setForm(null); load(); }} />
      )}
    </div>
  );
}
